import { useEffect, useRef } from 'react';
import {
  claimDesktopCallWindow,
  closeDesktopCallWindow,
  focusDesktopCallWindow,
  isDesktopCallWindow,
} from '@/features/desktop/callWindow';
import { useDesktopEvent } from '@/features/desktop/hooks/useDesktopEvent';

/**
 * How long a finished call stays on screen in its own window before the window
 * goes away by itself.
 *
 * Long enough to read who you were talking to and press "Call again"; short
 * enough that a call that ended twenty minutes ago is not still sitting in the
 * taskbar. Teams does the same thing for the same reason. Suspended entirely
 * when there is a voicemail to leave — that screen is a task, not a receipt.
 */
const ENDED_GRACE_MS = 20_000;

export interface CallWindowLifecycleInput {
  readonly callId: string;
  /** The call is over: hung up, declined, missed, or the far end left. */
  readonly ended: boolean;
  /**
   * This call ended by MOVING everybody somewhere else — merged into another
   * call, or turned into a meeting. Not an ending with a screen: the place they
   * were moved to is already opening.
   */
  readonly movedAway: boolean;
  /** The end screen is offering to leave a message. Do not close under it. */
  readonly canLeaveVoicemail: boolean;
  /** `useCallControls.hangup` — fire-and-forget, settles into `ended`. */
  readonly hangup: () => void;
}

/**
 * TIES THE WINDOW AND THE CALL TOGETHER, in all three directions.
 *
 * WINDOW → CALL. The shell keys a call window by the WINDOW, not by the call it
 * was opened for, because this document can move itself to a different call —
 * "Call again" on the ended screen, the consult leg of an attended transfer.
 * Every mount of a call surface inside a call window therefore claims the call
 * it is showing. Without it the shell's idea of who holds what goes stale, and
 * a workspace asked to open the call THIS window is already in opens a second
 * window on it: two media sessions publishing into one SFU room.
 *
 * CLOSE → LEAVE. Destroying the renderer would never send
 * `POST /calls/:id/hangup`, so the far end would keep hearing an open line
 * until a server-side sweeper closed the row. Nothing that exists today covers
 * this: `useCallLifecycle`'s unmount cleanup and the `pagehide` handler in
 * `useActivityMonitor` both only clear PRESENCE. So the shell intercepts the
 * first close, asks here, and destroys the window when the hangup has settled
 * (or when its own grace period runs out, whichever is first).
 *
 * ENDED → CLOSE. A window for a call that is over must not linger. It closes
 * itself once the end screen has had its moment — and at once when the call was
 * MERGED away, where there is no end screen and the survivor is already opening
 * in another window.
 *
 * All of them are no-ops outside a desktop call window: in a browser, and in the
 * desktop workspace window, `useDesktopEvent` never fires, `claim` resolves
 * true without asking anybody, and the effects return immediately.
 */
export function useCallWindowLifecycle({
  callId,
  ended,
  movedAway,
  canLeaveVoicemail,
  hangup,
}: CallWindowLifecycleInput): void {
  /* Refs, not deps: `useDesktopEvent` attaches its IPC listener exactly once
     and reads the latest handler through a ref of its own, so the handler must
     be able to see fresh values without being re-created. */
  const endedRef = useRef(ended);
  endedRef.current = ended;
  const hangupRef = useRef(hangup);
  hangupRef.current = hangup;
  const callIdRef = useRef(callId);
  callIdRef.current = callId;
  /** Set when the OS close button is what started this, not the hang-up button. */
  const closingByWindow = useRef(false);

  /*
   * THE BACKSTOP CLAIM. `useOpenCall` claims before it navigates, which covers
   * every route this app knows about; this covers the ones it does not — the
   * URL the window booted on, a reload, anything a future feature adds — by
   * claiming whatever call is actually on screen.
   *
   * A refusal means another live window is already in this call, so this
   * document must not stay in it: hand the user to the window that holds it and
   * take this one away rather than leave two rooms open on one call.
   */
  useEffect(() => {
    if (!isDesktopCallWindow() || callId === '') return;
    void claimDesktopCallWindow(callId).then((held) => {
      if (held || callIdRef.current !== callId) return;
      focusDesktopCallWindow(callId);
      closeDesktopCallWindow(callId);
    });
  }, [callId]);

  /*
   * The close request is addressed to THIS WINDOW — the shell sends it to one
   * webContents — so it is not gated on the call id it carries. It used to be,
   * and that is precisely how the OS close button stopped hanging up: the
   * request named the call the window was OPENED for, this document had since
   * moved to another, the ids disagreed and the handler returned. Nothing hung
   * up, and the shell destroyed the window three seconds later with the call
   * still live server-side.
   */
  useDesktopEvent('call-window-close-request', () => {
    if (!isDesktopCallWindow()) return;
    /* Already over: there is nothing to hang up and `ended` will not change
       again, so the effect below would never fire. Close now. */
    if (endedRef.current) {
      closeDesktopCallWindow(callIdRef.current);
      return;
    }
    closingByWindow.current = true;
    hangupRef.current();
  });

  /*
   * MOVED AWAY → GONE, AT ONCE.
   *
   * A merge ends this call and moves everybody on it into the surviving one; an
   * escalation ends it and moves them into a meeting. In a browser either one is
   * a single document changing rooms. Here it is two windows: the WORKSPACE
   * follows (`useTransferredCall` and `useEscalatedCall`, neither of which a
   * call window mounts) while THIS one is left holding a call that no longer
   * exists. Nothing else can take it away — `endedReason` is never set for
   * either, so the ended effect below never runs, and the grant is
   * `staleTime: Infinity, retry: false` so the "That call has ended" escape
   * never fires either. It stayed in the taskbar for the rest of the session,
   * still reporting live media, still claiming the media hotkeys and the tray
   * from the real call.
   *
   * No grace period: the user is already looking at where they were moved to.
   */
  useEffect(() => {
    if (!isDesktopCallWindow() || !movedAway) return undefined;
    closeDesktopCallWindow(callId);
    return undefined;
  }, [callId, movedAway]);

  useEffect(() => {
    if (!isDesktopCallWindow() || !ended) return undefined;
    if (closingByWindow.current) {
      /* The hangup the close button asked for has settled. Let the window go
         before the shell's grace timer has to. */
      closeDesktopCallWindow(callId);
      return undefined;
    }
    if (canLeaveVoicemail) return undefined;
    const timer = setTimeout(() => closeDesktopCallWindow(callId), ENDED_GRACE_MS);
    return () => clearTimeout(timer);
  }, [callId, ended, canLeaveVoicemail]);
}
